import React, { useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type {
  AaiSkill, CompanyDashboard, ControlEvent, DepartmentAggregate, OwnerAdoption,
} from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, NotMeasured, PageBody, PageHead,
  Panel, PanelHead, RouteMark, Stat, StatusChip,
} from '../../components/aa';
import { relativeTime } from '../partner/format';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { AssignBlockerDrawer, DemonstrationDrawer, PlanSupportDrawer } from '../champion/AdoptionDrawers';
import { SharePanel } from '../champion/SharePanel';
import { ACTION_KIND_LABEL, isClosed, isOpenRespondable, payloadText, shortName, sideOf, statusOf, subjectOf } from '../champion/actionStatus';
import { MIN_GROUP_SIZE, plural, readinessLine, teamName, teamsNeedingSupport, isProtected } from './trainer';

/*
 * The Trainer's AI Adoption: every team, and every action against them.
 *
 * Home answers "what needs me now". This page is the whole picture the trainer
 * targets from: each team's measured readiness, the actions open against them,
 * and the history of what support has already been delivered. A protected team
 * says so rather than showing a number nobody may see.
 */

type TeamState = 'needs_support' | 'protected' | 'ready';

const STATE_CHIP: Record<TeamState, { label: string; tone: 'warn' | 'private' | 'ok' }> = {
  needs_support: { label: 'Needs support', tone: 'warn' },
  protected: { label: 'Protected', tone: 'private' },
  ready: { label: 'Ready', tone: 'ok' },
};

const ORDER: Record<TeamState, number> = { needs_support: 0, ready: 1, protected: 2 };

const stateOf = (d: DepartmentAggregate, needing: DepartmentAggregate[]): TeamState => {
  if (isProtected(d)) return 'protected';
  if (needing.some((t) => t.department === d.department)) return 'needs_support';
  return 'ready';
};

type Drawer =
  | { kind: 'update'; event: ControlEvent }
  | { kind: 'support'; team: string | null }
  | { kind: 'blocker' }
  | { kind: 'demo' };

const TrainerAdoptionPage: React.FC = () => {
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not load team readiness.');
  const adoption = useAsync<OwnerAdoption>(() => aaiApi.getOwnerAdoption(), 'Could not load the adoption program.');
  const events = useAsync<{ events: ControlEvent[] }>(() => aaiApi.listControlEvents(), 'Could not load adoption actions.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load published Quick Wins.');
  const [drawer, setDrawer] = useState<Drawer | null>(null);
  const me = localStorage.getItem('user_email') || '';

  if (dashboard.loading) return <PageBody><Loading label="Loading AI adoption" /></PageBody>;
  if (dashboard.error || !dashboard.data) return <PageBody><ErrorNote message={dashboard.error || 'Something went wrong.'} /></PageBody>;

  const dash = dashboard.data;
  const all = events.data?.events || [];
  const open = all.filter(isOpenRespondable);
  const closed = all.filter(isClosed);
  const needing = teamsNeedingSupport(dash);
  const teamNames = (dash.departments || []).map(teamName).filter((n) => n !== 'Unassigned');
  const priorityNames = needing.map((t) => t.department);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);
  const cohorts = adoption.data?.cohorts || [];

  const departments = [...(dash.departments || [])].sort((a, b) => {
    const diff = ORDER[stateOf(a, needing)] - ORDER[stateOf(b, needing)];
    if (diff !== 0) return diff;
    return (a.maturity?.maturity_pct ?? 0) - (b.maturity?.maturity_pct ?? 0);
  });

  const reload = () => { events.reload(); adoption.reload(); dashboard.reload(); };

  const openFor = (department: string): ControlEvent | null =>
    open.find((e) => String((e.payload || {}).department || '').toLowerCase() === department.toLowerCase()) || null;

  let readinessValue: React.ReactNode = <NotMeasured reason="Protected" />;
  const pct = dash.company.maturity?.maturity_pct;
  const suppressed = dash.company.suppressed || Boolean(dash.company.maturity?.suppressed);
  if (!suppressed && pct !== null && pct !== undefined) readinessValue = `${pct}%`;

  const actionRow = (e: ControlEvent, label: string) => {
    const state = statusOf(e.status);
    let recipients = 'Nobody, record only';
    const sent = (e.payload || {}).sent_to;
    if (Array.isArray(sent) && sent.length > 0) recipients = sent.map((x) => shortName(String(x))).join(', ');
    let action = label;
    if (sideOf(e, me) === 'recipient') action = 'Respond';
    return (
      <ListRow
        key={e.id}
        mark={<RouteMark kind="adoption" glyph={(ACTION_KIND_LABEL[e.kind] || 'A').charAt(0)} size="lg" />}
        title={subjectOf(e)}
        badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
        desc={payloadText(e, 'response_note') || payloadText(e, 'note') || undefined}
        meta={[
          { label: 'Team', value: String((e.payload || {}).department || 'Whole company') },
          { label: 'Raised by', value: shortName(e.actor_email), foot: relativeTime(e.created_at) },
          { label: 'Sent to', value: recipients },
        ]}
        right={<Action size="sm" variant="quiet" arrow onClick={() => setDrawer({ kind: 'update', event: e })}>{action}</Action>}
      />
    );
  };

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="Trainer"
        title="AI Adoption"
        sub={[
          plural(departments.length, 'team', 'teams'),
          plural(needing.length, 'under the support line', 'under the support line'),
          plural(open.length, 'open action', 'open actions'),
        ].join(' · ')}
        actions={(
          <>
            <Action variant="quiet" onClick={() => setDrawer({ kind: 'support', team: priorityNames[0] || null })}>Plan support</Action>
            <Action variant="quiet" onClick={() => setDrawer({ kind: 'demo' })}>Schedule demonstration</Action>
            <Action variant="quiet" onClick={() => setDrawer({ kind: 'blocker' })}>Assign shared blocker</Action>
          </>
        )}
      />

      <div className="mb-5 flex flex-wrap gap-3">
        <Stat className="min-w-[220px] flex-1" label="Company readiness" value={readinessValue} evidence={`${dash.company.onboarded_count} of ${dash.enrolled} people onboarded`} />
        <Stat className="min-w-[220px] flex-1" label="Teams needing support" value={needing.length} tone={needing.length > 0 ? 'attention' : 'quiet'} evidence={`Measured under ${50}% readiness`} />
        <Stat className="min-w-[220px] flex-1" label="Cohorts" value={cohorts.length} evidence={cohorts.length > 0 ? cohorts.map((c) => c.name).slice(0, 2).join(' · ') : 'Cut a cohort to schedule sessions'} />
      </div>

      <Panel className="mb-5">
        <PanelHead
          kicker="Teams"
          title="Where each team stands"
          desc={`Team aggregates only. A group with fewer than ${MIN_GROUP_SIZE} onboarded people is protected and reports no score.`}
        />
        {departments.length === 0 && <EmptyState title="No teams yet" reason="Teams appear once the roster carries departments." />}
        {departments.length > 0 && (
          <Divided>
            {departments.map((t) => {
              const state = stateOf(t, needing);
              const look = STATE_CHIP[state];
              const openAction = openFor(t.department || '');
              return (
                <ListRow
                  key={t.department}
                  mark={<RouteMark kind="adoption" size="lg" />}
                  title={teamName(t)}
                  badge={<Chip tone={look.tone} dot>{look.label}</Chip>}
                  desc={readinessLine(t)}
                  foot={openAction ? `Open: ${ACTION_KIND_LABEL[openAction.kind] || openAction.kind} · ${statusOf(openAction.status).label}` : undefined}
                  right={(
                    <div className="flex flex-wrap items-center gap-2">
                      {openAction && <Action size="sm" variant="quiet" onClick={() => setDrawer({ kind: 'update', event: openAction })}>Open action</Action>}
                      {!openAction && <Action size="sm" variant="quiet" onClick={() => setDrawer({ kind: 'support', team: t.department })}>Plan support</Action>}
                    </div>
                  )}
                />
              );
            })}
          </Divided>
        )}
      </Panel>

      <SharePanel className="mb-5" />

      <Panel className="mb-5">
        <PanelHead
          kicker="Open actions"
          title={open.length === 0 ? 'Nothing open' : `${plural(open.length, 'action', 'actions')} in flight`}
          desc="Everything raised by or sent to the program team, until it is closed."
        />
        {open.length === 0 && (
          <EmptyState title="Nothing open" reason="Plan support, schedule a demonstration or assign a blocker and it appears here until it is closed." />
        )}
        {open.length > 0 && <Divided>{open.map((e) => actionRow(e, 'Update'))}</Divided>}
      </Panel>

      {closed.length > 0 && (
        <Panel>
          <PanelHead kicker="History" title={`${plural(closed.length, 'closed action', 'closed actions')}`} desc="Delivered, held, resolved, declined and withdrawn, newest first." />
          <Divided>
            {closed.slice(0, 12).map((e) => (
              <ListRow
                key={e.id}
                mark={<RouteMark kind="adoption" glyph={(ACTION_KIND_LABEL[e.kind] || 'A').charAt(0)} size="lg" />}
                title={subjectOf(e)}
                badge={<StatusChip status="record_only" label={statusOf(e.status).label} />}
                desc={payloadText(e, 'response_note') || undefined}
                foot={`${String((e.payload || {}).department || 'Whole company')} · ${relativeTime(payloadText(e, 'responded_at') || e.created_at)}`}
                right={<Action size="sm" variant="ghost" arrow onClick={() => setDrawer({ kind: 'update', event: e })}>Open</Action>}
              />
            ))}
          </Divided>
        </Panel>
      )}

      {drawer?.kind === 'update' && <ActionUpdateDrawer event={drawer.event} onUpdated={reload} onClose={() => setDrawer(null)} />}
      {drawer?.kind === 'support' && (
        <PlanSupportDrawer teams={teamNames} priority={priorityNames} initialTeam={drawer.team} onRecorded={reload} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'blocker' && <AssignBlockerDrawer events={all} onRecorded={reload} onClose={() => setDrawer(null)} />}
      {drawer?.kind === 'demo' && <DemonstrationDrawer skills={quickWins} teams={teamNames} onRecorded={reload} onClose={() => setDrawer(null)} />}
    </PageBody>
  );
};

export default TrainerAdoptionPage;
